import { ByocUploadContentService } from './../../service/upload-content.service';
import { Component, OnInit, ChangeDetectorRef, ViewChild, OnDestroy, ElementRef } from '@angular/core';
import { Subscription, tap } from 'rxjs';
import { ByocEpisodeService } from '../../service/episode.service';
import { ByocEpisodeFetchService } from './service/fetch.service';
import { Skill } from '@app/gamification';
import { DropdownItem } from '@app/shared/forms/dropdown-multi/model/dropdown.model';
import { FormArray, FormGroup } from '@angular/forms';
import { MessageService } from '@app/service/message.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { ByocService } from '@app/byoc/popup/service/byoc.service';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { SubtitlesControlsModel } from '@app/shared/subtitles-uploader/models';
import { array, control } from '../../../utils';

@Component({
    selector: 'app-byoc-episode-step-general',
    templateUrl: './general.component.html',
    styleUrls: ['./general.component.scss'],
    providers: [ByocEpisodeFetchService],
    standalone: false
})
export class ByocEpisodeStepGeneralComponent implements OnInit, OnDestroy {
    @ViewChild('box', { static: true })
    box?: ElementRef;

    @ViewChild('addQuestionTooltip') addQuestionTooltip!: NgbTooltip;

    saving = false;
    open = false;
    episodeTypesOptions: DropdownItem[] = [
        { id: 'video', name: this.translateService.instant('v3-global-video'), icon: 'content-video' },
        { id: 'podcast', name: this.translateService.instant('v3-global-podcast'), icon: 'content-podcast' },
        { id: 'document', name: this.translateService.instant('v3-global-document'), icon: 'content-document' },
        { id: 'article', name: this.translateService.instant('v3-global-article'), icon: 'content-article' },
        { id: 'presentation', name: this.translateService.instant('v3-global-presentation'), icon: 'content-presentation' },
    ];
    reuploadLoading$ = this.byocUploadContentService.reuploadLoading$;
    validating = false;
    private readonly subscriptions = new Subscription();

    readonly control = control;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private readonly byocEpisodeService: ByocEpisodeService,
        private readonly byocEpisodeFetchService: ByocEpisodeFetchService,
        private readonly messageService: MessageService,
        private readonly translateService: TranslateService,
        private readonly byocService: ByocService,
        private router: Router,
        private readonly byocUploadContentService: ByocUploadContentService,
    ) {}

    ngOnInit() {
        this.subscriptions.add(
            this.byocEpisodeFetchService.updatePsScrollBottomEvent
                .pipe(
                    tap(() => {
                        this.changeDetectorRef.detectChanges();
                        (<HTMLElement>this.box?.nativeElement).scrollTo(0, (<HTMLElement>this.box?.nativeElement).scrollHeight);
                    }),
                )
                .subscribe(),
        );
        this.subscriptions.add(
            this.byocEpisodeFetchService.updatePsScrollToEvent
                .pipe(
                    tap((position: number) => {
                        (<HTMLElement>this.box?.nativeElement).scrollTo(
                            0,
                            position - this.box?.nativeElement.getBoundingClientRect().top + this.box?.nativeElement.scrollTop,
                        );
                    }),
                )
                .subscribe(),
        );
        this.subscriptions.add(
            this.controlDuration.valueChanges.subscribe(() => {
                this.correctDuration();
            }),
        );
        this.subscriptions.add(
            this.byocEpisodeService.form.valueChanges.subscribe(() => {
                this.patchForm();
            }),
        );
        if (this.infoNotLoaded) {
            this.messageService.setMessage(this.translateService.instant('v3-byoc-popup-create-episode-info-not-loaded'), 'warning');
        }
        if (this.uploadContentType && this.uploadContentType.length > 0) {
            this.messageService.clear();
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    toggleOpen(): void {
        if (this.byocEpisodeFetchService.editing) {
            return;
        }
        this.open = !this.open;
        this.changeDetectorRef.detectChanges();
        (<HTMLElement>this.box?.nativeElement).scrollTo(0, (<HTMLElement>this.box?.nativeElement).scrollHeight);
    }

    openTooltip() {
        this.addQuestionTooltip.open();
    }

    closeTooltip() {
        this.addQuestionTooltip.close();
    }

    get uploadContentType() {
        return this.byocUploadContentService.uploadType;
    }

    get controlLink() {
        return control(this.byocEpisodeService.form.get('source')!.get('url'));
    }

    get controlTitle() {
        return control(this.byocEpisodeService.form.get('title'));
    }

    get controlContent() {
        return control(this.byocEpisodeService.form.get('content'));
    }

    get controlNotes() {
        return control(this.byocEpisodeService.form.get('notes'));
    }

    get infoNotLoaded(): boolean {
        return !this.byocEpisodeService.infoLoaded;
    }

    get controlDuration() {
        return control(this.byocEpisodeService.form.get('duration'));
    }

    get controlQuestions() {
        return array(this.byocEpisodeService.form.get('questions'));
    }

    get controlSkills() {
        return control(this.byocEpisodeService.form.get('skills'));
    }

    get controlType() {
        return control(this.byocEpisodeService.form.get('type'));
    }

    get editing(): boolean {
        return this.byocEpisodeFetchService.editing;
    }

    get durationLoaded(): boolean {
        return this.byocEpisodeService.durationLoaded;
    }

    get skipPosition(): boolean {
        return this.byocEpisodeService.skipSteps.includes('position');
    }

    get linkLabel(): string {
        switch (this.byocEpisodeService.form.get('type')!.value) {
            case 'video':
                return this.translateService.instant('v3-byoc-popup-create-episode-fetch-link-label-video');
            case 'podcast':
                return this.translateService.instant('v3-byoc-popup-create-episode-fetch-link-label-podcast');
            case 'document':
                return this.translateService.instant('v3-byoc-popup-create-episode-fetch-link-label-document');
            case 'article':
                return this.translateService.instant('v3-byoc-popup-create-episode-fetch-link-label-article');
            case 'presentation':
                return this.translateService.instant('v3-byoc-popup-create-episode-fetch-link-label-presentation');
            default:
                return '';
        }
    }

    get canEditEpisodeAndUploadNewContent() {
        return (
            this.byocUploadContentService.uploadType !== null &&
            this.byocUploadContentService.uploadType?.length > 0 &&
            this.byocUploadContentService.episodeToEditId !== null &&
            this.byocUploadContentService.episodeToEditId?.length > 0
        );
    }

    /**
     * media Type
     */
    get cannotEmbedVideo(): boolean {
        return (
            this.byocEpisodeService.form.get('type')!.value === 'video' &&
            this.byocEpisodeService.form.get('source')!.value.provider === 'youtube' &&
            !this.byocEpisodeService.form.get('source')!.value.embedded
        );
    }

    get subtitlesArrayForm() {
        return <FormArray<FormGroup<SubtitlesControlsModel>>>this.byocEpisodeService.form.get('hostedCustomContent')!.get('subtitles');
    }

    reuploadAndSaveAsDraft() {
        if (this.editing || this.invalid) {
            return;
        }
        this.byocEpisodeService.form.patchValue({ status: 1 });
        this.byocUploadContentService.editEpisodeAndUploadNewContent();
    }

    reuploadAndPublish() {
        if (this.editing || this.invalid) {
            return;
        }
        this.byocEpisodeService.form.patchValue({ status: 2 });
        this.byocEpisodeService.step = 'users';
        this.changeDetectorRef.markForCheck();
    }

    /**
     * On Close Click
     */
    cancel() {
        if (this.byocEpisodeFetchService.editing) {
            return;
        }
        this.byocEpisodeService.close('btn-close');
    }

    get invalid(): boolean {
        return this.controlDuration.invalid || this.controlContent.invalid || this.controlTitle.invalid || this.validating;
    }

    get isInCourseCreatingFlow() {
        return this.byocEpisodeService.isFromOutside;
    }

    /**
     * On Next Click
     */
    next() {
        if (this.byocEpisodeService.processing || this.byocEpisodeFetchService.editing || this.invalid) {
            this.controlDuration.markAsPristine();
            this.changeDetectorRef.detectChanges();
            return;
        }

        this.byocEpisodeService.step = 'course';
        if (this.byocEpisodeService.skipSteps.includes('course')) {
            this.byocEpisodeService.step = 'position';
        }
        this.changeDetectorRef.markForCheck();
    }

    /**
     * On Back Click
     */
    back() {
        if (this.byocEpisodeFetchService.editing) {
            return;
        }
        this.byocEpisodeService.step = 'link';
        this.changeDetectorRef.markForCheck();
    }

    /**
     * Skill
     */
    selectedSkills(skills: Skill[]) {
        this.byocEpisodeService.selectedSkills = skills;
    }

    /**
     * Save as Draft
     */
    saveAsDraftClick(): void {
        if (this.invalid) {
            return;
        }

        this.byocEpisodeService.form.patchValue({ status: 1 });

        // show loader
        this.saving = true;
        this.changeDetectorRef.detectChanges();

        // save as draft
        this.subscriptions.add(
            this.byocService
                .createEpisode(this.byocEpisodeService.getEpisodeDataToSent())
                .pipe(
                    tap(() => {
                        if (this.byocUploadContentService.uploadType) {
                            this.byocEpisodeService.step = 'uploaded-content-info';
                            this.changeDetectorRef.markForCheck();
                            return;
                        }
                        const learningItemCreatedMessage = this.translateService.instant('v3-byoc-popup-create-episode-alert-save-as-draft');
                        if (this.router.url === '/manager/settings/content/byoc/list') {
                            this.messageService.setMessage(learningItemCreatedMessage, 'info');
                            this.byocEpisodeService.close('btn-close');
                        } else {
                            this.messageService.setRouteMessage(learningItemCreatedMessage, 'info');
                            this.router.navigate(['/manager', 'settings', 'content', 'byoc', 'list']);
                        }
                    }),
                )
                .subscribe(),
        );
    }

    /**
     * Publish Click
     */
    usersClick(): void {
        if (this.invalid) {
            return;
        }

        this.byocEpisodeService.form.patchValue({ status: 2 });
        this.byocEpisodeService.step = 'users';
    }

    onValidating(validating: boolean) {
        this.validating = validating;
    }

    get selectedEpisodeSkills() {
        return this.byocEpisodeService.selectedSkills;
    }

    /**
     * Corrects Duration to 99:59:59
     */
    private correctDuration() {
        const { h: hours, m: minutes, s: seconds } = this.controlDuration.value;
        const duration = {
            h: hours.replace(/\D/g, ''),
            m: minutes.replace(/\D/g, ''),
            s: seconds.replace(/\D/g, ''),
        };

        duration.h = Number(duration.h) > 99 ? '99' : duration.h;
        duration.m = Number(duration.m) > 60 ? '59' : duration.m;
        duration.s = Number(duration.s) > 60 ? '59' : duration.s;

        this.controlDuration.patchValue(duration, { emitEvent: false });
    }

    private patchForm(): void {
        const { title, content, notes } = <{ title: string; content: string; notes: string }>this.byocEpisodeService.form.value;

        this.byocEpisodeService.form.patchValue(
            {
                title: title.split('<').join('').split('>').join(''),
                content: content.split('<').join('').split('>').join(''),
                notes: notes.split('<').join('').split('>').join(''),
            },
            { emitEvent: false },
        );
    }
}
